(() => {
  if (window.__affiliatePricingBootstrapped) return;
  window.__affiliatePricingBootstrapped = true;

  const AFFILIATE_REF_COOKIE = 'ich_aff_ref_present';
  const AFFILIATE_TIER_COOKIE = 'ich_aff_tier';
  const VALID_TIERS = new Set([16, 20]);
  const AFFILIATE_UPSELL_SELECTOR = '[data-affiliate-upsell-price-root="true"]';
  let runtimeCartDiscount = null;
  const googleOfferSchemas = new WeakMap();

  const getCookie = (name) => {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  };

  const hasAffiliateReference = () => getCookie(AFFILIATE_REF_COOKIE) === '1';

  const getAffiliateTier = () => {
    const tier = Number(getCookie(AFFILIATE_TIER_COOKIE));
    return VALID_TIERS.has(tier) ? tier : null;
  };

  const isValidDiscountPercentage = (percentage) =>
    Number.isFinite(percentage) && percentage > 0 && percentage < 100;

  const getCartDiscountFromMarkup = (priceRoot) => ({
    active: priceRoot.dataset.cartDiscountActive === 'true',
    percentage: Number(priceRoot.dataset.cartDiscountPercentage || 0),
    code: priceRoot.dataset.cartDiscountCode || '',
  });

  const getCartDiscountFromCart = (cart) => {
    const discounts = Array.isArray(cart?.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : [];

    if (!discounts.length) return { active: false, percentage: 0, code: '' };
    if (discounts.length !== 1) return { active: true, percentage: 0, code: '' };

    const discount = discounts[0];
    const percentage = Number(discount.value || 0);
    const canEstimate =
      discount.value_type === 'percentage' &&
      discount.target_type === 'line_item' &&
      discount.target_selection === 'all' &&
      isValidDiscountPercentage(percentage);

    return {
      active: true,
      percentage: canEstimate ? percentage : 0,
      code: discount.title || discount.key || '',
    };
  };

  // Codes with their own rate (window.ichessCodeRates in layout/theme.liquid), e.g. R8V3K6 from Google Ads.
  // Shopify keeps a code from a discount link in the discount_code cookie until checkout.
  // These codes are applied quietly (from an ad link), so the note never names them.
  const getCodeRate = (code) => {
    const normalizedCode = String(code || '').trim().toUpperCase();
    const policy = window.ichessGoogleOfferPolicy;
    if (policy?.code === normalizedCode &&
        (Date.now() < Date.parse(policy.start) || Date.now() > Date.parse(policy.end))) return NaN;
    return Number((window.ichessCodeRates || {})[normalizedCode]);
  };

  const getCodeRatePreview = () => {
    const code = getCookie('discount_code')
      .split(',')
      .find((candidate) => isValidDiscountPercentage(getCodeRate(candidate)));
    return code ? { percentage: getCodeRate(code), source: 'code', code } : null;
  };

  const getGoogleOfferPreview = (priceRoot) => {
    const percentage = Number(priceRoot.dataset.googleOfferPercentage || 0);
    const code = priceRoot.dataset.googleOfferCode || '';
    return isValidDiscountPercentage(percentage) && getCodeRate(code) === percentage
      ? { percentage, code, source: 'google' } : null;
  };

  const getPricePreview = (priceRoot) => {
    const runtimeDiscount = runtimeCartDiscount;
    const markupDiscount = getCartDiscountFromMarkup(priceRoot);
    const affiliateTier = hasAffiliateReference() ? getAffiliateTier() : null;
    const publicDiscountPercentage = Number(priceRoot.dataset.publicDiscountPercentage || 0);
    const googleOffer = getGoogleOfferPreview(priceRoot);
    const isPublicCartDiscount = (discount) =>
      googleOffer && discount?.active &&
      discount.code?.trim().toUpperCase() === (priceRoot.dataset.publicDiscountCode || '').trim().toUpperCase();

    if (runtimeDiscount?.active && isValidDiscountPercentage(runtimeDiscount.percentage) && !isPublicCartDiscount(runtimeDiscount)) {
      return { percentage: runtimeDiscount.percentage, source: 'cart', code: runtimeDiscount.code };
    }

    if (markupDiscount.active && isValidDiscountPercentage(markupDiscount.percentage) && !isPublicCartDiscount(markupDiscount)) {
      return { percentage: markupDiscount.percentage, source: 'cart', code: markupDiscount.code };
    }

    if ((runtimeDiscount?.active && !isPublicCartDiscount(runtimeDiscount)) ||
        (markupDiscount.active && !isPublicCartDiscount(markupDiscount))) {
      return null;
    }

    const codeRatePreview = getCodeRatePreview();
    if (codeRatePreview) {
      return codeRatePreview;
    }

    // The old 16/20 tier is a heuristic. The reviewed Google reference has a
    // known rate and must not become 16% while BixGrow is still loading.
    const reference = new URLSearchParams(window.location.search).get('bg_ref') || getCookie('ich_aff_last_bg_ref');
    if (googleOffer && reference === window.ichessGoogleOfferPolicy?.reference) return googleOffer;

    if (isValidDiscountPercentage(affiliateTier)) {
      return { percentage: affiliateTier, source: 'affiliate' };
    }

    if (googleOffer) return googleOffer;

    if (isValidDiscountPercentage(publicDiscountPercentage)) {
      return { percentage: publicDiscountPercentage, source: 'public' };
    }

    return null;
  };

  const updatePricePreviewNote = (priceRoot, pricePreview) => {
    const note = priceRoot.querySelector('[data-price-preview-note]');
    if (!note) return;

    const publicDiscountCode = priceRoot.dataset.publicDiscountCode || '';
    if (pricePreview.source === 'cart' && pricePreview.code && !isValidDiscountPercentage(getCodeRate(pricePreview.code))) {
      note.textContent = (note.dataset.noteCart || 'Estimated price with “__CODE__” applied. Confirm at checkout.').replace('__CODE__', () => pricePreview.code);
    } else if (pricePreview.source === 'affiliate') {
      note.textContent = note.dataset.noteAffiliate || 'Estimated partner price. Confirm your code at checkout.';
    } else if (pricePreview.source === 'public' && publicDiscountCode) {
      note.textContent = (note.dataset.notePublic || 'Estimated price. The __CODE__ discount applies automatically at checkout.').replace('__CODE__', () => publicDiscountCode);
    } else {
      note.textContent = note.dataset.noteDefault || 'Estimated discounted price. Confirm the final amount at checkout.';
    }
  };

  // Promotion text blocks ("Promotion text" on in the product section): a visitor with their own discount
  // (code with its own rate, partner tier) sees that percent instead of the store-wide promotion.
  const updateOwnOfferTexts = (priceRoot, pricePreview) => {
    const container = priceRoot.closest('.product__info-container');
    if (!container) return;

    const isOwnOffer =
      Boolean(pricePreview) &&
      (pricePreview.source === 'code' ||
        pricePreview.source === 'google' ||
        pricePreview.source === 'affiliate' ||
        (pricePreview.source === 'cart' && isValidDiscountPercentage(getCodeRate(pricePreview.code))));
    // A code from the link is applied automatically; a partner tier without one still needs the partner's code
    const isAutomatic = pricePreview?.source === 'google' || Boolean(getCookie('discount_code'));

    container.querySelectorAll('[data-own-offer-text]').forEach((block) => {
      const publicText = block.querySelector('[data-public-offer]');
      const ownText = block.querySelector('[data-own-offer]');
      if (!publicText || !ownText) return;

      const template = isAutomatic ? block.dataset.ownOfferAuto : block.dataset.ownOfferPartner;
      const showOwn = isOwnOffer && Boolean(template);
      if (showOwn) ownText.innerHTML = template.replace('__PERCENT__', () => String(pricePreview.percentage));
      ownText.hidden = !showOwn;
      publicText.hidden = showOwn;
    });

    document.documentElement.classList.remove('has-own-offer');
  };

  const formatMoney = (cents, currencyCode) => {
    const currency = currencyCode || 'USD';
    const locale = document.documentElement.lang || undefined;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  };

  const detectMoneyTemplate = (baseMoney) => {
    const normalized = (baseMoney || '').replace(/\u00a0/g, ' ').trim();
    const numberMatch = normalized.match(/[-+]?\d[\d.,\s]*\d|[-+]?\d/);
    if (!numberMatch) return null;

    const numberRaw = numberMatch[0].trim();
    const prefix = normalized.slice(0, numberMatch.index);
    const suffix = normalized.slice(numberMatch.index + numberRaw.length);

    const lastComma = numberRaw.lastIndexOf(',');
    const lastDot = numberRaw.lastIndexOf('.');

    let decimalSep = null;
    let thousandSep = '';
    let decimals = 0;

    if (lastComma !== -1 && lastDot !== -1) {
      decimalSep = lastComma > lastDot ? ',' : '.';
      thousandSep = decimalSep === ',' ? '.' : ',';
      decimals = numberRaw.split(decimalSep).pop().replace(/\D/g, '').length;
    } else if (lastComma !== -1 || lastDot !== -1) {
      decimalSep = lastComma !== -1 ? ',' : '.';
      const fractionalLength = numberRaw.split(decimalSep).pop().replace(/\D/g, '').length;

      if (fractionalLength === 2) {
        decimals = 2;
      } else {
        decimalSep = null;
      }

      if (!decimalSep) {
        thousandSep = lastComma !== -1 ? ',' : '.';
      }
    }

    if (decimalSep) {
      const integerPartRaw = numberRaw.split(decimalSep)[0];
      const thousandMatch = integerPartRaw.match(/[.,\s](?=\d{3}(?:[.,\s]|$))/);
      if (thousandMatch) thousandSep = thousandMatch[0];
    }

    return { prefix, suffix, decimals, decimalSep, thousandSep };
  };

  const formatMoneyLikeBase = (cents, baseMoney, currencyCode) => {
    const template = detectMoneyTemplate(baseMoney);
    if (!template) return formatMoney(cents, currencyCode);

    const value = cents / 100;
    const fixed = value.toFixed(template.decimals);
    let [intPart, fracPart] = fixed.split('.');

    if (template.thousandSep) {
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, template.thousandSep);
    }

    const numberPart =
      template.decimals > 0 && template.decimalSep ? `${intPart}${template.decimalSep}${fracPart}` : intPart;

    return `${template.prefix}${numberPart}${template.suffix}`;
  };

  const resetAffiliatePreview = (priceRoot) => {
    const priceContainer = priceRoot.querySelector('.price__container');
    const saleBadge = priceRoot.querySelector('.price__badge-sale');
    const preview = priceRoot.querySelector('[data-affiliate-preview]');

    if (priceContainer) priceContainer.hidden = false;
    if (saleBadge) saleBadge.hidden = false;
    if (preview) preview.hidden = true;

    priceRoot.classList.remove('price--affiliate-preview');
    priceRoot.removeAttribute('data-affiliate-tier');
    priceRoot.removeAttribute('data-price-preview-source');
  };

  // Only the alternate Google view has this marker. Recalculate from immutable
  // Shopify base cents, never from a price that already includes a discount.
  const syncGoogleOfferSchema = (priceRoot, pricePreview) => {
    const productInfo = priceRoot.closest('product-info');
    const script = productInfo?.querySelector('[data-google-offer-structured-data]');
    if (!script) return;
    try {
      if (!googleOfferSchemas.has(script)) {
        googleOfferSchemas.set(script, {
          document: JSON.parse(script.textContent),
          basePrices: JSON.parse(script.dataset.basePrices),
        });
      }
      const snapshot = googleOfferSchemas.get(script);
      const schema = JSON.parse(JSON.stringify(snapshot.document));
      const percentage = pricePreview?.percentage || 0;
      for (const variant of schema.hasVariant || []) {
        const id = new URL(variant.offers.url, window.location.origin).searchParams.get('variant');
        const base = snapshot.basePrices[id];
        if (!Number.isSafeInteger(base) || base <= 0) return;
        const final = Math.round(base * (100 - percentage) / 100);
        variant.offers.price = (final / 100).toFixed(2);
        if (percentage > 0) {
          variant.offers.priceSpecification.price = (base / 100).toFixed(2);
        } else {
          delete variant.offers.priceSpecification;
        }
      }
      const json = JSON.stringify(schema).replace(/</g, '\\u003c');
      if (script.textContent !== json) script.textContent = json;
      const meta = document.querySelector('[data-google-offer-meta]');
      if (meta) {
        const base = Number(priceRoot.dataset.affiliateBasePrice);
        meta.content = (Math.round(base * (100 - percentage) / 100) / 100).toFixed(2);
      }
    } catch (error) {
      console.warn('Could not update Google offer structured data.', error);
    }
  };

  const applyAffiliatePrice = (priceRoot) => {
    if (!(priceRoot instanceof HTMLElement)) return;

    const affiliateEligible = priceRoot.dataset.affiliateEligible === 'true';
    const basePrice = Number(priceRoot.dataset.affiliateBasePrice || 0);
    const baseMoney = priceRoot.dataset.affiliateBaseMoney || '';
    const currency = priceRoot.dataset.affiliateCurrency || 'USD';
    const pricePreview = getPricePreview(priceRoot);
    const shouldPreview = affiliateEligible && basePrice > 0 && pricePreview;

    updateOwnOfferTexts(priceRoot, shouldPreview ? pricePreview : null);
    syncGoogleOfferSchema(priceRoot, shouldPreview ? pricePreview : null);

    if (!shouldPreview) {
      resetAffiliatePreview(priceRoot);
      return;
    }

    const preview = priceRoot.querySelector('[data-affiliate-preview]');
    const compare = priceRoot.querySelector('[data-affiliate-preview-compare]');
    const finalPrice = priceRoot.querySelector('[data-affiliate-preview-final]');
    const priceContainer = priceRoot.querySelector('.price__container');
    const saleBadge = priceRoot.querySelector('.price__badge-sale');

    if (!preview || !compare || !finalPrice) {
      resetAffiliatePreview(priceRoot);
      return;
    }

    const discountedPrice = Math.round((basePrice * (100 - pricePreview.percentage)) / 100);
    compare.textContent = baseMoney || formatMoney(basePrice, currency);
    finalPrice.textContent = formatMoneyLikeBase(discountedPrice, baseMoney, currency);
    updatePricePreviewNote(priceRoot, pricePreview);

    if (priceContainer) priceContainer.hidden = true;
    if (saleBadge) saleBadge.hidden = true;
    preview.hidden = false;

    priceRoot.classList.add('price--affiliate-preview');
    priceRoot.setAttribute('data-affiliate-tier', `${pricePreview.percentage}`);
    priceRoot.setAttribute('data-price-preview-source', pricePreview.source);
  };

  const resetAffiliateUpsellPrice = (priceRoot) => {
    const baseCompare = priceRoot.querySelector('[data-fp-base-compare]');
    const baseCurrent = priceRoot.querySelector('[data-fp-base-current]');
    const affiliateCompare = priceRoot.querySelector('[data-fp-affiliate-compare]');
    const affiliateCurrent = priceRoot.querySelector('[data-fp-affiliate-current]');

    if (baseCompare) baseCompare.hidden = false;
    if (baseCurrent) baseCurrent.hidden = false;
    if (affiliateCompare) affiliateCompare.hidden = true;
    if (affiliateCurrent) affiliateCurrent.hidden = true;

    priceRoot.classList.remove('fp-price--affiliate-preview');
    priceRoot.removeAttribute('data-affiliate-tier');
    priceRoot.removeAttribute('data-price-preview-source');
  };

  const applyAffiliateUpsellPrice = (priceRoot) => {
    if (!(priceRoot instanceof HTMLElement)) return;

    const affiliateEligible = priceRoot.dataset.affiliateEligible === 'true';
    const basePrice = Number(priceRoot.dataset.affiliateBasePrice || 0);
    const baseMoney = priceRoot.dataset.affiliateBaseMoney || '';
    const currency = priceRoot.dataset.affiliateCurrency || 'USD';
    const pricePreview = getPricePreview(priceRoot);
    const shouldPreview = affiliateEligible && basePrice > 0 && pricePreview;

    if (!shouldPreview) {
      resetAffiliateUpsellPrice(priceRoot);
      return;
    }

    const baseCompare = priceRoot.querySelector('[data-fp-base-compare]');
    const baseCurrent = priceRoot.querySelector('[data-fp-base-current]');
    const affiliateCompare = priceRoot.querySelector('[data-fp-affiliate-compare]');
    const affiliateCurrent = priceRoot.querySelector('[data-fp-affiliate-current]');

    if (!baseCurrent || !affiliateCompare || !affiliateCurrent) {
      resetAffiliateUpsellPrice(priceRoot);
      return;
    }

    const discountedPrice = Math.round((basePrice * (100 - pricePreview.percentage)) / 100);
    affiliateCompare.textContent = baseMoney || formatMoney(basePrice, currency);
    affiliateCurrent.textContent = formatMoneyLikeBase(discountedPrice, baseMoney, currency);

    if (baseCompare) baseCompare.hidden = true;
    baseCurrent.hidden = true;
    affiliateCompare.hidden = false;
    affiliateCurrent.hidden = false;

    priceRoot.classList.add('fp-price--affiliate-preview');
    priceRoot.setAttribute('data-affiliate-tier', `${pricePreview.percentage}`);
    priceRoot.setAttribute('data-price-preview-source', pricePreview.source);
  };

  const bootAffiliatePrices = (root = document) => {
    root.querySelectorAll('[data-affiliate-price-root="true"]').forEach(applyAffiliatePrice);
    root.querySelectorAll(AFFILIATE_UPSELL_SELECTOR).forEach(applyAffiliateUpsellPrice);
  };

  const syncRuntimeCartDiscount = (cart) => {
    runtimeCartDiscount = getCartDiscountFromCart(cart);
    bootAffiliatePrices();
  };

  const fetchRuntimeCartDiscount = () => {
    const shopifyRoot = window.Shopify?.routes?.root || '/';
    fetch(`${shopifyRoot}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then(syncRuntimeCartDiscount)
      .catch(() => {
        runtimeCartDiscount = null;
        bootAffiliatePrices();
      });
  };

  window.refreshAffiliatePricing = (root = document) => {
    bootAffiliatePrices(root);
    fetchRuntimeCartDiscount();
  };

  const applyDynamicAffiliateUpsellPrices = (node) => {
    if (!(node instanceof Element)) return;
    if (node.matches(AFFILIATE_UPSELL_SELECTOR)) applyAffiliateUpsellPrice(node);
    node.querySelectorAll(AFFILIATE_UPSELL_SELECTOR).forEach(applyAffiliateUpsellPrice);
  };

  const observeDynamicAffiliateUpsells = () => {
    if (!document.body) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(applyDynamicAffiliateUpsellPrices);
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      bootAffiliatePrices();
    });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (Array.isArray(event?.cartData?.cart_level_discount_applications)) {
        syncRuntimeCartDiscount(event.cartData);
      } else {
        fetchRuntimeCartDiscount();
      }
    });
  }

  document.addEventListener('product-info:loaded', () => {
    bootAffiliatePrices();
  });

  document.addEventListener('shopify:section:load', (event) => {
    bootAffiliatePrices(event?.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bootAffiliatePrices();
      observeDynamicAffiliateUpsells();
      fetchRuntimeCartDiscount();
    });
  } else {
    bootAffiliatePrices();
    observeDynamicAffiliateUpsells();
    fetchRuntimeCartDiscount();
  }
})();
